import { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../../services/axiosConfig';
import { extractErrorMessage } from '../../../services/axiosConfig';

/**
 * Token usage data structure (matches backend CurrentTokenUsageResponse)
 */
export interface TokenUsageData {
  tokenUsed: number;
  tokenLimit: number | null;  // null = unlimited tokens
  tokensAvailable: number | null;  // null = unlimited (infinite available)
  usagePercentage: number;
}

/**
 * Hook para buscar dados de uso de tokens em tempo real do banco de dados
 *
 * Este hook faz chamada direta à API para obter dados frescos de token_used,
 * evitando problemas de cache que ocorrem quando se usa o objeto user.
 *
 * @returns Estado e funções para gerenciar dados de uso de tokens
 *
 * @example
 * ```tsx
 * const { tokenData, loading, error, refetch } = useTokenUsage();
 *
 * if (loading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage>{error}</ErrorMessage>;
 *
 * return <div>{tokenData?.tokenUsed.toLocaleString()}</div>;
 * ```
 */
export const useTokenUsage = () => {
  const [tokenData, setTokenData] = useState<TokenUsageData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTokenUsage = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.get('/users/tokens/current');

      if (response.data?.success && response.data?.data) {
        setTokenData(response.data.data);
      } else {
        throw new Error('Formato de resposta inválido');
      }
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Não foi possível carregar os dados de uso de tokens'
      );
      setError(errorMessage);
      console.error('Error fetching token usage:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTokenUsage();
  }, [fetchTokenUsage]);

  return {
    tokenData,
    loading,
    error,
    refetch: fetchTokenUsage
  };
};
